import { consumeBfcacheAuthReloadMarker, consumeBuildReloadOrigin, consumeCachedDocumentAuthReloadMarker, consumeChunkRecoveryMarker, consumeDepositReturnFullLoadMarker, consumeDomDesyncReloadMarker, consumeIdleBuildReloadMarker, consumeOutdatedBuildReloadMarker, consumeStaleBootReloadMarker, captureVueError, createApmVueErrorHandler, createEarlyVueErrors, defineNuxtPlugin, followsStalePurge, dropInjectedScriptOverflowErrors, isKnownCrawler, labelDomDesyncErrors, labelRecoveredChunkErrors, observeInpAttribution, onNuxtReady, propagateTraceparent, takeBootBuildCheck, useApmLabels, useApmUser, useNuxtApp, usePreferredReducedMotion, useRuntimeConfig, useRouter, useState } from '#imports'

export default defineNuxtPlugin({
    name: 'apm-plugin',
    parallel: true,
    setup() {
        const config = useRuntimeConfig()
        const router = useRouter()
        const nuxtApp = useNuxtApp()
        const { register, stamp } = useApmLabels()
        const { applyUser } = useApmUser()
        const reducedMotion = usePreferredReducedMotion()
        const pageLoad = useState<{ traceId: string, spanId: string } | undefined>('apm:pageLoad')
        // Read before a page translator can rewrite it.
        const servedLang = document.documentElement.lang
        // Hooked at boot: the agent installs on Nuxt ready, after hydration, and nothing reports a Vue error before that.
        // Not for crawlers: NuxtRoot keeps their errors from the installed handler, so they'd be reported only until then.
        const earlyErrors = createEarlyVueErrors()
        if (!isKnownCrawler(navigator.userAgent)) nuxtApp.hook('vue:error', earlyErrors.hold)

        let mountedAt: number | undefined

        nuxtApp.hook('app:mounted', () => {
            mountedAt = performance.now()
        })

        let hydratedAt: number | undefined
        nuxtApp.hook('app:suspense:resolve', () => {
            hydratedAt ??= performance.now()
        })

        // `inpAttribution` is ours, not an agent option.
        const { inpAttribution, ...apmConfig } = (config.public.apm || {}) as { inpAttribution?: boolean, [key: string]: unknown }
        // The page path from each time on, so a tap is put on the page it happened on.
        const paths: Array<[number, string]> = [[0, location.pathname]]
        if (inpAttribution) router.afterEach(to => void paths.push([performance.now(), to.path]))
        const pathAt = (time: number) => paths.findLast(([since]) => since <= time)?.[1]
        // Started here rather than with the agent, so interactions during startup are seen as they happen.
        const attachInpAttribution = inpAttribution ? observeInpAttribution(() => hydratedAt, pathAt) : undefined

        onNuxtReady(async () => {
            // Skip under Vitest: onNuxtReady fires after test setup, and the
            // dynamic import below can resolve after the Nuxt test environment
            // is torn down — producing an EnvironmentTeardownError that makes
            // vitest exit non-zero even though every test passes.
            if (import.meta.env.VITEST) return

            const sqlDump = /\/__nuxt_content\/.+\/sql_dump\.txt$/

            // Before the import: the agent wraps fetch on import and must wrap this (ADR 0005).
            if (apmConfig.active !== false) {
                propagateTraceparent(() => nuxtApp.vueApp.config.globalProperties.$apm?.getCurrentTransaction?.(), sqlDump)
            }

            // @ts-expect-error No declaration files in package / typescript not supported
            const { ApmVuePlugin } = await import('@elastic/apm-rum-vue').catch((error: unknown) => {
                earlyErrors.flush()
                throw error
            })

            const ignoreTransactions = (apmConfig as { ignoreTransactions?: Array<string | RegExp> }).ignoreTransactions ?? []

            nuxtApp.vueApp.use(ApmVuePlugin, {
                router,
                // Installed below instead, to name `<script setup>` components.
                captureErrors: false,
                config: {
                    ...apmConfig,
                    ignoreTransactions: [...ignoreTransactions, sqlDump],
                    // propagateTraceparent writes the header instead.
                    distributedTracing: false,
                    // Puts the page load at the root of the server render's trace.
                    ...(pageLoad.value && {
                        pageLoadTraceId: pageLoad.value.traceId,
                        pageLoadSpanId: pageLoad.value.spanId,
                        pageLoadSampled: true,
                    }),
                },
            })

            const apm = nuxtApp.vueApp.config.globalProperties.$apm

            if (apm?.isActive?.()) {
                nuxtApp.vueApp.config.errorHandler = createApmVueErrorHandler(apm, nuxtApp.vueApp.config.errorHandler)
            }

            // Same dump fetch, but recorded as a span when a transaction is active — drop those too.
            apm?.addFilter?.((payload: { transactions?: Array<{ spans?: Array<{ name?: string }> }> }) => {
                payload?.transactions?.forEach((tr) => {
                    tr.spans = tr.spans?.filter(span => !span.name?.includes('sql_dump.txt'))
                })
                return payload
            })

            apm?.addFilter?.((payload: { errors?: Array<{ culprit?: string, exception?: { message?: string } }> }) =>
                dropInjectedScriptOverflowErrors(payload, navigator.userAgent))

            // Stale-chunk import errors are answered with a reload by the chunk-reload
            // plugin; label them so triage can tell handled errors from unrecovered ones.
            apm?.addFilter?.(labelRecoveredChunkErrors)

            apm?.addFilter?.((payload: Parameters<typeof labelDomDesyncErrors>[0]) => labelDomDesyncErrors(payload, servedLang))

            // This page load completes a chunk-error recovery reload; label it so recovery
            // rate is measurable against chunk_error_recovered-labeled errors.
            if (consumeChunkRecoveryMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ chunk_reload_completed: true })
            }

            const transaction = apm?.getCurrentTransaction?.()
            if (mountedAt !== undefined && transaction?.type === 'page-load') {
                transaction.addLabels?.({ mounted_ms: Math.round(mountedAt) })
            }

            if (consumeDomDesyncReloadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ dom_desync_reload_completed: true })
            }

            if (consumeBfcacheAuthReloadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ bfcache_auth_reload_completed: true })
            }

            if (consumeCachedDocumentAuthReloadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ cached_document_auth_reload_completed: true })
            }

            // Page loads that moved a tab off an outdated build, so stale-build recovery is measurable.
            if (consumeStaleBootReloadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ stale_boot_reload_completed: true })
            }
            if (consumeOutdatedBuildReloadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ outdated_build_reload_completed: true })
            }
            if (consumeIdleBuildReloadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ idle_build_reload_completed: true })
            }
            if (followsStalePurge(document.cookie)) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ stale_client_purge_completed: true })
            }
            const reloadOrigin = consumeBuildReloadOrigin()
            if (reloadOrigin) {
                apm?.getCurrentTransaction?.()?.addLabels?.(reloadOrigin)
            }
            // What the start check cost when it ran and did not reload.
            const bootCheck = takeBootBuildCheck()
            if (bootCheck) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ boot_build_check: bootCheck.result, boot_build_check_ms: bootCheck.ms })
            }
            if (consumeDepositReturnFullLoadMarker()) {
                apm?.getCurrentTransaction?.()?.addLabels?.({ deposit_return_full_load: true })
            }

            register(() => ({ prefers_reduced_motion: reducedMotion.value === 'reduce' }))

            // Stamp every transaction with labels contributed by feature modules.
            apm?.observe?.('transaction:start', stamp)

            attachInpAttribution?.(apm)

            apm?.getCurrentTransaction?.()?.addLabels?.({ prefers_reduced_motion: reducedMotion.value === 'reduce' })

            // Apply the logged-in user's identity (ECS user.id) to the agent once it is
            // ready. This is global/session context (not per-transaction like labels);
            // feature consumers re-apply it on login changes via useApmUser.
            applyUser(nuxtApp.vueApp.config.globalProperties.$apm)

            // After applyUser, in the same task as the handler install: the agent reads the user into an error when it is
            // captured, and no error can reach both the installed handler and the flush.
            if (apm?.isActive?.()) {
                earlyErrors.flush((args, at) => captureVueError(apm, args, { captured_before_apm: true, captured_before_apm_ms: Math.round(at) }))
            }
            else {
                earlyErrors.flush()
            }

            if (apm) await nuxtApp.callHook('apm:ready', apm)
        })
    },
})
